import { Navigate } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useRoles, hasRole } from '@/hooks/useRoles';
import { useLabOS } from '@/lib/labos';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requireAdmin?: boolean;
  redirectTo?: string;
}

/**
 * Protected route component that checks if user has required role or admin status
 * Redirects to home page if user doesn't have the required permissions
 */
export default function ProtectedRoute({
  children,
  requiredRole,
  requireAdmin = false,
  redirectTo = '/',
}: ProtectedRouteProps): ReactElement {
  const { roles, loading: rolesLoading } = useRoles();
  const { user, loading: userLoading } = useLabOS();

  const loading = rolesLoading || userLoading;

  // Show loading state while fetching roles
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Check admin requirement first (admins bypass all other checks)
  if (requireAdmin && !user?.isAdmin) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user has required role (if specified)
  if (requiredRole && !user?.isAdmin && !hasRole(roles, requiredRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
